/**
 * Design system tokens for saas_front.
 * Source of truth: .claude/skills/design/design-system.md
 *
 * Import from here instead of hardcoding values anywhere in the app.
 */

// ─── Colors ──────────────────────────────────────────────────────────────────

/** Primary text color. Never use pure black. */
export const NAVY = '#15183A';

/** Primary CTA / button background. */
export const YELLOW = '#F9D00A';

/** Greyscale */
export const LIGHT_GREY = '#E6E6E6';

/** Subtle Navy tint — used for input box backgrounds and secondary surfaces. */
export const SURFACE_TINT = 'rgba(21, 24, 58, 0.03)';

/** Row hover state — slightly stronger tint than SURFACE_TINT. */
export const SURFACE_HOVER = 'rgba(21, 24, 58, 0.06)';
/** Complementary cyan accent — icons, tag fills and secondary highlights. */
export const CYAN = '#47BCD8';

// ─── Channel brand colors ─────────────────────────────────────────────────────

/** WhatsApp brand green — used for the WhatsApp channel badge and icon. */
export const WHATSAPP_GREEN = '#25D366';

/** Meta brand blue — used for the Meta channel badge. */
export const META_BLUE = '#1877F2';

// ─── Semantic status colors ───────────────────────────────────────────────────
// Used for performance indicators (CRM rate, health badges, status chips).

/** Good / healthy / above-target. */
export const STATUS_GOOD = '#53B721';

/**
 * Same hue as STATUS_GOOD, darkened for type: the fill green reads 2.6:1 on a light surface, under
 * the 3:1 that even large text has to clear. Use it whenever the good state is a word, not a shape.
 */
export const STATUS_GOOD_TEXT = '#3D8618';

/** Warning / attention / below-target. */
export const STATUS_WARN = '#CF5C09';

/** Neutral / no-data / muted. */
export const STATUS_NEUTRAL = 'rgba(21, 24, 58, 0.3)';

/** Error / danger — matches the MUI theme error.main (design-system §10). */
export const STATUS_ERROR = '#E7233B';

// ─── Advance-progress ramp ────────────────────────────────────────────────────
// Portability advance chip (Mueve Seguro Desgravamen/Cesantía). No-advance reads
// as neutral grey (LIGHT_GREY); progress deepens along a single lightened-Navy
// hue until it lands on solid Navy near completion. Pair with getProgressTextColor.

/** Early advance (1–30%) — light Navy tint. */
export const PROGRESS_LOW = '#D6D9EC';

/** Mid advance (31–70%) — stronger lightened Navy. */
export const PROGRESS_MID = '#AEB4DC';

// ─── Surface hierarchy ────────────────────────────────────────────────────────
// Use tonal shifts instead of 1px borders to separate sections.

/** Base page background. */
export const SURFACE = '#F9FAFD';

/** Section / sidebar background (1 tier above base). */
export const SURFACE_CONTAINER_LOW = '#F0F4F8';

/** Cards / interactive elements (sits on surface-container-low). */
export const SURFACE_CONTAINER_LOWEST = '#ffffff';

/** Neutral chat backdrop — warm grey, used behind message bubbles. */
export const SURFACE_CHAT = '#F4F4F6';

// ─── Message bubbles ──────────────────────────────────────────────────────────
// Tone per sender in the ticket conversation view.

/** Admin / equipo (saliente, derecha). */
export const BUBBLE_ADMIN = 'rgba(249, 208, 10, 0.15)'; // = alpha(YELLOW, 0.15)

/** Usuario no-admin (entrante, izquierda). Azul tenue derivado de META_BLUE. */
export const BUBBLE_USER = 'rgba(24, 119, 242, 0.10)';

/** Rollbar / mensajes automáticos del sistema (entrante, izquierda). Igual que SURFACE_CONTAINER_LOW. */
export const BUBBLE_ROLLBAR = '#F0F4F8';

// ─── Elevation & shadows ─────────────────────────────────────────────────────
// Shadows are tinted with Navy to feel like a natural part of the environment.

/** Standard ambient shadow for floating elements. */
export const SHADOW = '0px 12px 32px rgba(21, 24, 58, 0.06)';

/** Subtle card shadow — tight spread so it doesn't bleed past the container padding. */
export const SHADOW_CARD = '0px 1px 3px rgba(21, 24, 58, 0.14)'; // , 0px 2px 10px rgba(21, 24, 58, 0.05)';
export const SHADOW_DARK_CARD = '1px 1px 3px rgba(21, 24, 58, 0.3)'; // , 0px 2px 10px rgba(21, 24, 58, 0.05)';

// ─── Glassmorphism ────────────────────────────────────────────────────────────
// Apply to dropdowns, modals, and hovering action buttons.

export const GLASS_BLUR = 'blur(16px)';
export const GLASS_BACKGROUND = 'rgba(249, 250, 253, 0.85)';

// ─── Border radius ────────────────────────────────────────────────────────────

/** "Coin" style — used on primary buttons and main containers. */
export const RADIUS_COIN = '24px';

/** Standard card radius. */
export const RADIUS_CARD = '16px';

/** Compact radius for menu items and chips. */
export const RADIUS_ITEM = '8px';

// ─── Mobile layout ────────────────────────────────────────────────────────────

export const MOBILE_HAMBURGER_WIDTH = 56;
export const MOBILE_HAMBURGER_HEIGHT = 56;
export const MOBILE_HAMBURGER_LEFT = 8;
export const MOBILE_FILTER_BAR_TOP = 8;
/** MasterFilter card left offset on mobile: button left + width + 4px gap. */
export const MOBILE_FILTER_BAR_ML = 68;
/** Page padding-top on mobile for pages with MasterFilter: top + height + gap. */
export const MOBILE_PAGE_PT = 72;
